import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, XCircle, Loader2, Home } from "lucide-react";
import { BillingSEO } from "@/components/SEO";
import { MainContent } from "@/components/MainContent";

export default function Billing() {
  const [, navigate] = useLocation();
  const [status, setStatus] = useState<"loading" | "success" | "canceled">("loading");
  const [sessionId, setSessionId] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get("success");
    const canceled = params.get("canceled");
    const session_id = params.get("session_id");

    if (success === "1") {
      setStatus("success");
      setSessionId(session_id);
    } else if (canceled === "1") {
      setStatus("canceled");
    } else {
      setStatus("loading");
    }
  }, []);

  return (
    <>
      <BillingSEO />
      <MainContent className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          {status === "loading" && (
            <>
              <div className="mx-auto mb-4">
                <Loader2 className="h-12 w-12 animate-spin text-primary" />
              </div>
              <CardTitle>Processing...</CardTitle>
              <CardDescription>Please wait while we process your payment</CardDescription>
            </>
          )}
          
          {status === "success" && (
            <>
              <div className="mx-auto mb-4">
                <CheckCircle className="h-12 w-12 text-green-500" />
              </div>
              <CardTitle className="text-green-600">Order Confirmed!</CardTitle>
              <CardDescription className="space-y-2">
                <p>Thank you for choosing AI Systems Studio!</p>
                <p>Your custom solution has been assigned to our specialist team and is now being prepared.</p>
                <div className="mt-4 p-3 bg-muted/50 rounded-lg text-left text-sm">
                  <p className="font-semibold mb-2">Your Project Team:</p>
                  <ul className="space-y-1 text-muted-foreground">
                    <li>• Project Lead: Sarah Chen</li>
                    <li>• Technical Architect: Marcus Williams</li>
                    <li>• Quality Specialist: Priya Patel</li>
                  </ul>
                </div>
                <p className="text-sm text-muted-foreground">
                  ⏱️ <strong>Estimated Delivery:</strong> 24-48 hours
                </p>
                <p className="text-sm">
                  You'll receive an email from your assigned specialist when your system is ready for review.
                </p>
              </CardDescription>
            </>
          )}
          
          {status === "canceled" && (
            <>
              <div className="mx-auto mb-4">
                <XCircle className="h-12 w-12 text-red-500" />
              </div>
              <CardTitle className="text-red-600">Payment Canceled</CardTitle>
              <CardDescription>
                Your payment was canceled. No charges were made.
              </CardDescription>
            </>
          )}
        </CardHeader>
        
        <CardContent className="space-y-4">
          {sessionId && status === "success" && (
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-xs text-muted-foreground">Session ID</p>
              <p className="text-xs font-mono break-all">{sessionId}</p>
            </div>
          )}
          
          <Button 
            className="w-full" 
            onClick={() => navigate("/")}
          >
            <Home className="mr-2 h-4 w-4" />
            Return to Home
          </Button>
          
          {status === "success" && (
            <div className="text-center text-xs text-muted-foreground pt-2">
              <p>Questions? Contact your project team:</p>
              <p className="font-mono mt-1">support@saasai.com | (555) 123-4567</p>
            </div>
          )}
          
          {status === "canceled" && (
            <Button 
              variant="outline"
              className="w-full" 
              onClick={() => navigate("/build")}
            >
              Try Again
            </Button>
          )}
        </CardContent>
      </Card>
    </MainContent>
    </>
  );
}
